import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useNear } from '@/features/location/store'
import { nextPage } from '@/features/products/api'
import { api } from '@/lib/api'
import { qk } from '@/lib/queryKeys'
import { cleanQuery, isSearchable } from '@/lib/search'
import type { ProductCard, SearchResponse, ShopCard, SuggestResponse } from '@/types/api'

export type SearchTab = 'products' | 'shops'

export interface SearchParams {
  q: string
  town: string | null
  type: SearchTab
}

type Result<T extends SearchTab> = SearchResponse<T extends 'shops' ? ShopCard : ProductCard>

/**
 * GET /search/ — most relevant first (BR-8), nearby shops next with a shared location (BR-28);
 * never re-sorted on the device. Debounce `q` in the caller. Only fires from 2 characters (the API
 * returns nothing below that).
 */
export function useSearch<T extends SearchTab>(params: SearchParams & { type: T }) {
  const q = cleanQuery(params.q)
  const near = useNear()
  return useInfiniteQuery({
    queryKey: qk.search({ ...params, q, near }),
    queryFn: ({ pageParam, signal }) =>
      api<Result<T>>('/search/', {
        query: { q, town: params.town, type: params.type, near, page: pageParam, page_size: 24 },
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: nextPage,
    enabled: isSearchable(q),
    placeholderData: keepPreviousData,
  })
}

/**
 * GET /search/suggest/ — suggestions under the search box while typing (D-91). Debounce `q` in
 * the caller; errors (rate limit, offline) just show no suggestions.
 */
export function useSuggest(q: string, town: string | null) {
  const term = cleanQuery(q)
  return useQuery({
    queryKey: qk.suggest(term.toLowerCase(), town),
    queryFn: ({ signal }) =>
      api<SuggestResponse>('/search/suggest/', { query: { q: term, town }, signal }),
    enabled: isSearchable(term),
    staleTime: 5 * 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  })
}
